import { useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Col, Container, Row, Button } from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";

import * as Yup from "yup";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";

import AddressBook from "../AddressBook/AddressBook";

import { configuratorActions } from "../../store/configurator-slice";
import { addressBookActions } from "../../store/address-book-slice";

function EcardRecipientForm(props) {
  const dispatch = useDispatch();

  // get current request state
  const formStep = useSelector((state) => state.configurator.FormStep);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const [showAddressBook, setShowAddressBook] = useState(false);

  // init form validation schema
  const formSchema = Yup.object().shape({
    ecardSendToFirstName: Yup.string().required("Required"),
    ecardSendToLastName: Yup.string().required("Required"),
    emailAddress: Yup.string()
      .required("Required")
      .email("Please enter a valid email address"),
    emailAddressConfirm: Yup.string()
      .required("Required")
      .email("Please enter a valid email address")
      .oneOf([Yup.ref("emailAddress"), null], "E-mail addresses don't match"),
  });

  const formSubmitHandler = (values) => {
    dispatch(configuratorActions.setEcardRecipient(values));
    FormHelpers.scrollToTop();
  };

  const previousClickHandler = (values) => {
    dispatch(configuratorActions.setFormStep("Personalize"));
    FormHelpers.scrollToTop();
  };

  function toggleAddressBook(show) {
    setShowAddressBook(show);
  }

  const formRef = useRef();
  const addressUpdateFromAddressBookHandler = (address) => {
    formRef.current.values.ecardSendToFirstName = address.FirstName;
    formRef.current.values.ecardSendToLastName = address.LastName;
    formRef.current.values.emailAddress = address.EmailAddress;
    formRef.current.values.emailAddressConfirm = address.EmailAddress;
    toggleAddressBook(false);
  };

  function addToAddressBookClickHandler(values) {
    let address = {
      EmailAddress: values.emailAddress,
      FirstName: values.ecardSendToFirstName,
      LastName: values.ecardSendToLastName
    };

    address.UserId = currentRequest.DonorInfo.Id;

    dispatch(addressBookActions.setAddingAddress(address));
    setShowAddressBook(true);
  }

  return (
    <div
      className="cfg-input-form cfg-ecard-recipient"
      data-step="ecard-recipient"
    >
      <Formik
        initialValues={{
          ecardSendToFirstName: requestDetails.EcardSendToFirstName,
          ecardSendToLastName: requestDetails.EcardSendToLastName,
          emailAddress: requestDetails.EcardEmail,
          emailAddressConfirm: requestDetails.EcardEmail,
          ecardName: requestDetails.EcardName,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row className="mb-3">
                <Col xs="12" className="text-center">
                  <Button type="button" size="lg" color="primary" onClick={() => toggleAddressBook(true)}>
                    Display Address Book
                  </Button>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="ecardSendToFirstName">
                    eCard Recipient First Name:
                  </label>
                  <Field
                    name="ecardSendToFirstName"
                    id="ecardSendToFirstName"
                    component={ReactstrapInput}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="ecardSendToLastName">
                  eCard Recipient Last Name:
                  </label>
                  <Field
                    name="ecardSendToLastName"
                    id="ecardSendToLastName"
                    component={ReactstrapInput}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                    <label className="required" htmlFor="emailAddress">
                    eCard Recipient E-mail Address:
                    </label>
                    <Field
                      name="emailAddress"
                      type="email"
                      id="emailAddress"
                      component={ReactstrapInput}
                    />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="emailAddressConfirm">
                    Please Re-enter E-mail Address:
                  </label>
                  <Field
                    name="emailAddressConfirm"
                    type="email"
                    id="emailAddressConfirm"
                    onKeyUp={(e) => {
                      handleChange(e);
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                    component={ReactstrapInput}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label htmlFor="ecardName">Name of Sender:</label>
                  <Field
                    name="ecardName"
                    id="ecardName"
                    component={ReactstrapInput}
                  />
                </Col>
              </Row>
              {(Object.keys(touched).length > 0 && Object.keys(errors).length === 0) && (
                <Row className="mb-2">
                  <Col xs="12" className="text-center">
                    <a onClick={() => addToAddressBookClickHandler(values)}>Add Recipient to My Address Book</a>
                  </Col>
                </Row>
              )}
            </Container>
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={previousClickHandler}
              className="btn-secondary"
            >
              Previous
            </Button>{" "}
            <Button type="submit" size="sm" color="primary" className="float-end">
              Next
            </Button>
          </Form>
        )}
      </Formik>
      {showAddressBook && (
        <AddressBook
          formStep={formStep}
          showAddressBook={showAddressBook}
          onToggleAddressBookClickHandler={toggleAddressBook}
          onSelectClickHandler={addressUpdateFromAddressBookHandler}
        />
      )}
    </div>
  );
}

export default EcardRecipientForm;
